import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import { createEquipment, fetchFactoriesForManagement } from '../../services/api';
import './EquipmentRegisterPage.css';

function EquipmentRegisterPage() {
    const navigate = useNavigate();
    const [factories, setFactories] = useState([]);
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        equipmentCode: '',
        equipmentName: '',
        model: '',
        factoryId: '',
        lineName: '',
        serialNumber: '',
        manufacturer: '',
        equipmentType: 'INJECTION_MOLDING',
        installDate: '',
        warrantyEndDate: '',
        status: 'IDLE',
        maxTemperature: '',
        maxPressure: '',
        ratedPower: '',
        targetCycleTime: '',
        cavityCount: '',
        lastInspectionDate: '',
        nextMaintenanceDate: '',
        description: ''
    });

    // 공장 목록 로드
    useEffect(() => {
        const loadFactories = async () => {
            try {
                setLoading(true);
                const data = await fetchFactoriesForManagement();
                setFactories(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('공장 목록 조회 실패:', err);
                setFactories([]);
            } finally {
                setLoading(false);
            }
        };

        loadFactories();
    }, []);


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // API 요청 데이터 구성
            const equipmentData = {
                factoryId: parseInt(formData.factoryId),
                lineName: formData.lineName || null,
                equipmentCode: formData.equipmentCode,
                equipmentName: formData.equipmentName,
                model: formData.model,
                manufacturer: formData.manufacturer,
                serialNumber: formData.serialNumber,
                equipmentType: formData.equipmentType,
                installDate: formData.installDate || null,
                warrantyEndDate: formData.warrantyEndDate || null,
                maxTemperature: formData.maxTemperature ? parseFloat(formData.maxTemperature) : null,
                maxPressure: formData.maxPressure ? parseFloat(formData.maxPressure) : null,
                ratedPower: formData.ratedPower ? parseFloat(formData.ratedPower) : null,
                targetCycleTime: formData.targetCycleTime ? parseInt(formData.targetCycleTime) * 1000 : null, // 초를 밀리초로 변환
                cavityCount: formData.cavityCount ? parseInt(formData.cavityCount) : null,
                status: formData.status,
                productName: null,
                currentMoldId: null,
                specifications: null,
                lastInspectionDate: formData.lastInspectionDate || null,
                nextMaintenanceDate: formData.nextMaintenanceDate || null,
                description: formData.description || null
            };

            
            const result = await createEquipment(equipmentData);

            alert('설비가 성공적으로 등록되었습니다.');
            navigate('/equipment-list');
        } catch (err) {
            console.error('설비 등록 실패:', err);

            // 에러 메시지 상세 처리
            if (err.message.includes('already exists')) {
                alert('이미 존재하는 설비 코드입니다. 다른 코드를 사용해주세요.');
            } else {
                alert('설비 등록에 실패했습니다. 입력 정보를 확인해주세요.');
            }
        }
    };

    const handleCancel = () => {
        navigate('/equipment-list');
    };

    return (
        <MainLayout>
            <div className="equipment-register-container">
                {/* Header Section */}
                <div className="equipment-register-header">
                    <div className="equipment-register-header-left">
                        <div className="equipment-register-title">설비 등록</div>
                        <p className="equipment-register-subtitle">새로운 설비 정보 생성</p>
                    </div>
                </div>

                {/* Form Section */}
                <div className="equipment-register-content">
                    <form onSubmit={handleSubmit}>
                        {/* 기본 정보 섹션 */}
                        <div className="equipment-form-section">
                            <h3 className="equipment-section-title">기본 정보</h3>
                            <div className="equipment-form-grid">
                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        설비 코드 <span className="equipment-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="equipmentCode"
                                        className="equipment-form-input"
                                        placeholder="예: E001"
                                        value={formData.equipmentCode}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        설비명 <span className="equipment-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="equipmentName"
                                        className="equipment-form-input"
                                        placeholder="예: 사출기 #1"
                                        value={formData.equipmentName}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        모델 <span className="equipment-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="model"
                                        className="equipment-form-input"
                                        placeholder="예: LS-350H"
                                        value={formData.model}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        소속 공장 <span className="equipment-required">*</span>
                                    </label>
                                    <div className="equipment-dropdown-container">
                                        <select
                                            name="factoryId"
                                            className="equipment-form-select"
                                            value={formData.factoryId}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">공장 선택</option>
                                            {factories.map(factory => (
                                                <option key={factory.id} value={factory.id}>{factory.factoryName}</option>
                                            ))}
                                        </select>
                                        <svg className="equipment-dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        라인
                                    </label>
                                    <input
                                        type="text"
                                        name="lineName"
                                        className="equipment-form-input"
                                        placeholder="예: 라인 A"
                                        value={formData.lineName}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        제조사 <span className="equipment-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="manufacturer"
                                        className="equipment-form-input"
                                        placeholder="제조사명 입력"
                                        value={formData.manufacturer}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        시리얼넘버 <span className="equipment-required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="serialNumber"
                                        className="equipment-form-input"
                                        placeholder="설비 시리얼넘버 입력"
                                        value={formData.serialNumber}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        설치일
                                    </label>
                                    <input
                                        type="date"
                                        name="installDate"
                                        className="equipment-form-input"
                                        value={formData.installDate}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        보증 종료일
                                    </label>
                                    <input
                                        type="date"
                                        name="warrantyEndDate"
                                        className="equipment-form-input"
                                        value={formData.warrantyEndDate}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        초기 상태 <span className="equipment-required">*</span>
                                    </label>
                                    <div className="equipment-dropdown-container">
                                        <select
                                            name="status"
                                            className="equipment-form-select"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="RUNNING">가동</option>
                                            <option value="IDLE">대기</option>
                                            <option value="STOPPED">정지</option>
                                            <option value="MAINTENANCE">점검</option>
                                            <option value="FAULT">고장</option>
                                            <option value="OFFLINE">오프라인</option>
                                        </select>
                                        <svg className="equipment-dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 운영 사양 섹션 */}
                        <div className="equipment-form-section">
                            <h3 className="equipment-section-title">사양 정보</h3>
                            <div className="equipment-form-grid">
                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        최대 온도 (°C)
                                    </label>
                                    <input
                                        type="number"
                                        name="maxTemperature"
                                        className="equipment-form-input"
                                        placeholder="예: 350"
                                        value={formData.maxTemperature}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        최대 압력 (MPa)
                                    </label>
                                    <input
                                        type="number"
                                        name="maxPressure"
                                        className="equipment-form-input"
                                        placeholder="예: 180"
                                        value={formData.maxPressure}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        정격 전력 (kW)
                                    </label>
                                    <input
                                        type="number"
                                        name="ratedPower"
                                        className="equipment-form-input"
                                        placeholder="예: 35.0"
                                        value={formData.ratedPower}
                                        onChange={handleInputChange}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        목표 사이클 타임 (초)
                                    </label>
                                    <input
                                        type="number"
                                        name="targetCycleTime"
                                        className="equipment-form-input"
                                        placeholder="예: 45"
                                        value={formData.targetCycleTime}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        캐비티 수
                                    </label>
                                    <input
                                        type="number"
                                        name="cavityCount"
                                        className="equipment-form-input"
                                        placeholder="예: 4"
                                        value={formData.cavityCount}
                                        onChange={handleInputChange}
                                        min="0"
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        마지막 점검일
                                    </label>
                                    <input
                                        type="date"
                                        name="lastInspectionDate"
                                        className="equipment-form-input"
                                        value={formData.lastInspectionDate}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="equipment-form-group">
                                    <label className="equipment-form-label">
                                        다음 정비일
                                    </label>
                                    <input
                                        type="date"
                                        name="nextMaintenanceDate"
                                        className="equipment-form-input"
                                        value={formData.nextMaintenanceDate}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="equipment-form-group equipment-form-group-full">
                                    <label className="equipment-form-label">비고</label>
                                    <textarea
                                        name="description"
                                        className="equipment-form-textarea"
                                        placeholder="추가 설명 입력"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows="4"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="equipment-form-actions">
                            <button type="button" className="equipment-btn-secondary" onClick={handleCancel}>
                                취소
                            </button>
                            <button type="submit" className="equipment-btn-primary">
                                설비 등록
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </MainLayout>
    );
}

export default EquipmentRegisterPage;
